import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { ProjectTypeRef } from '@/dto/scenario/ProjectReference';
import VueI18n, { LocaleMessages } from 'vue-i18n';
import { hasProperties, Path, PathWithValue } from '@/helpers/pathHelper';

interface Dependencies {
  loadFile: (filename: string) => Promise<{data: unknown}>;
  refreshLocaleMessages: (localeMessages: LocaleMessages) => void;
  getPaths:(currentObject: unknown, currentPath?: Path) => PathWithValue[];
  assignToPath: (target: unknown, path: Path, value: unknown, createMissingValues?: boolean) => boolean,
  getValueFromPath: (obj: unknown, path: Path, createMissingProperties?: boolean) => unknown,
  i18n: VueI18n;
  errorHandler: (error: unknown) => void;
}

interface SiteMapSpecification {
  displayed?: boolean;
}

interface SiteMapSpecifications {
  [key: string]: SiteMapSpecifications | SiteMapSpecification
}

function isSiteMapSpecification(value: unknown): value is SiteMapSpecification {
  return hasProperties(value, 'displayed');
}

export const buildSiteMapModule = ({
  loadFile,
  getPaths,
  assignToPath,
  getValueFromPath,
  refreshLocaleMessages,
  i18n,
  errorHandler
}: Dependencies) => {
  @Module({
    namespaced: true
  })
  class SiteMapModule extends VuexModule {

    private siteMapSpecifications: SiteMapSpecifications | null = null;

    get getSiteMapSpecifications() {
      return this.siteMapSpecifications;
    }

    @Action({ rawError: true })
    async loadSiteMapLocales(projectType: ProjectTypeRef) {
      try {
        const { data } = await loadFile(`${projectType}_site_map_locales`);
        const messages: LocaleMessages = i18n.messages;
        getPaths(data).forEach(siteMapLocal => {
          try {
            assignToPath(messages, siteMapLocal.path, siteMapLocal.value, true);
          } catch (error) {
            errorHandler(error);
          }
        });
        refreshLocaleMessages(messages);
      } catch (error) {
        errorHandler(error);
      }
    }

    @Action({ rawError: true })
    async loadSiteMapSpecifications(projectType: ProjectTypeRef) {
      try {
        const { data } = await loadFile(`${projectType}_site_map_specifications`);
        this.context.commit('setSiteMapSpecifications', data);
      } catch (error) {
        errorHandler(error);
      }
    }

    @Mutation
    private setSiteMapSpecifications(siteMapSpecifications: SiteMapSpecifications) {
      this.siteMapSpecifications = siteMapSpecifications;
    }

    get rule() {
      return (path: string): SiteMapSpecification => {
        const rule = getValueFromPath(this.siteMapSpecifications, path.split('.'));
        if (!isSiteMapSpecification(rule)) {
          errorHandler(new Error(`Rule for section '${path}' does not exist`));
          return { displayed: true };
        }
        return rule;
      };
    }
  }

  return SiteMapModule;
};
